import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import CloseButton from "../Customs/Button/CrossButton";
import {
  createExpense,
  updateExpense,
  uploadExpenseDocument,
} from "../Services/expenses";
import styles from "./AddExpensePanel.module.css";

interface NamedOption {
  value: string;
  label: string;
}

interface AccountOption {
  account_id: number;
  account_name: string;
  account_type: string;
}

interface VendorOption {
  vendor_id?: number;
  user_id?: number;
  company_name?: string;
  username?: string;
}

interface ExpenseEditData {
  expense_id: number;
  expense_date?: string;
  category?: string;
  description?: string;
  amount?: number | string;
  payment_method?: string;
  paid_from?: string;
  cash_bank_account_id?: number;
  vendor_id?: number;
  vendor_payee?: string;
  reference_number?: string;
  notes?: string;
}

interface AddExpensePanelProps {
  open: boolean;
  mode?: "add" | "edit";
  editData?: ExpenseEditData | null;
  categories: NamedOption[];
  paymentMethods: NamedOption[];
  paidFromOptions: NamedOption[];
  accounts: AccountOption[];
  vendors: VendorOption[];
  onClose: () => void;
  onSaved: () => void;
}

function todayYmd(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function toYmd(value?: string): string {
  if (!value) return todayYmd();
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1] : todayYmd();
}

function defaultPaidFrom(paymentMethod: string): string {
  if (paymentMethod === "CASH") return "CASH";
  if (paymentMethod === "UPI" || paymentMethod === "BANK_TRANSFER") return "BANK";
  return "OTHER";
}

const AddExpensePanel: React.FC<AddExpensePanelProps> = ({
  open,
  mode = "add",
  editData,
  categories,
  paymentMethods,
  paidFromOptions,
  accounts,
  vendors,
  onClose,
  onSaved,
}) => {
  const isEdit = mode === "edit";
  const [expenseDate, setExpenseDate] = useState(todayYmd());
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [paidFrom, setPaidFrom] = useState("CASH");
  const [accountId, setAccountId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [vendorPayee, setVendorPayee] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredAccounts = useMemo(
    () => accounts.filter((a) => !paidFrom || a.account_type === paidFrom),
    [accounts, paidFrom]
  );

  useEffect(() => {
    if (!open) return;
    setError(null);
    setFile(null);
    if (isEdit && editData) {
      setExpenseDate(toYmd(editData.expense_date));
      setCategory(editData.category || "");
      setDescription(editData.description || "");
      setAmount(editData.amount != null ? String(editData.amount) : "");
      setPaymentMethod(editData.payment_method || "CASH");
      setPaidFrom(editData.paid_from || "CASH");
      setAccountId(editData.cash_bank_account_id ? String(editData.cash_bank_account_id) : "");
      setVendorId(editData.vendor_id ? String(editData.vendor_id) : "");
      setVendorPayee(editData.vendor_payee || "");
      setReferenceNumber(editData.reference_number || "");
      setNotes(editData.notes || "");
      return;
    }
    const method = paymentMethods[0]?.value || "CASH";
    const from = defaultPaidFrom(method);
    setExpenseDate(todayYmd());
    setCategory(categories[0]?.value || "");
    setDescription("");
    setAmount("");
    setPaymentMethod(method);
    setPaidFrom(from);
    setVendorId("");
    setVendorPayee("");
    setReferenceNumber("");
    setNotes("");
    const match = accounts.find((a) => a.account_type === from);
    setAccountId(match ? String(match.account_id) : "");
  }, [open, isEdit, editData, categories, paymentMethods, accounts]);

  useEffect(() => {
    if (!open || isEdit) return;
    const match = filteredAccounts[0];
    setAccountId(match ? String(match.account_id) : "");
  }, [paidFrom, open, isEdit, filteredAccounts]);

  if (!open) return null;

  const handlePaymentMethodChange = (value: string) => {
    setPaymentMethod(value);
    if (!isEdit) {
      setPaidFrom(defaultPaidFrom(value));
    }
  };

  const handleVendorChange = (value: string) => {
    setVendorId(value);
    if (!value) return;
    const vendor = vendors.find(
      (v) => String(v.vendor_id || v.user_id) === value
    );
    if (vendor && !vendorPayee) {
      setVendorPayee(vendor.company_name || vendor.username || "");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!expenseDate) {
      setError("Expense date is required");
      return;
    }
    if (!category) {
      setError("Expense category is required");
      return;
    }
    if (!description.trim()) {
      setError("Expense description is required");
      return;
    }
    if (!isEdit && (!amount || Number(amount) <= 0)) {
      setError("Amount must be greater than zero");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (isEdit && editData) {
        await updateExpense(editData.expense_id, {
          description: description.trim(),
          vendor_id: vendorId ? Number(vendorId) : null,
          vendor_payee: vendorPayee,
          reference_number: referenceNumber,
          notes,
        });
        if (file) {
          await uploadExpenseDocument(editData.expense_id, file);
        }
        toast.success("Expense updated");
      } else {
        const formData = new FormData();
        formData.append("expense_date", expenseDate);
        formData.append("category", category);
        formData.append("description", description.trim());
        formData.append("amount", amount);
        formData.append("payment_method", paymentMethod);
        formData.append("paid_from", paidFrom);
        if (accountId) formData.append("cash_bank_account_id", accountId);
        if (vendorId) formData.append("vendor_id", vendorId);
        if (vendorPayee) formData.append("vendor_payee", vendorPayee);
        if (referenceNumber) formData.append("reference_number", referenceNumber);
        if (notes) formData.append("notes", notes);
        if (file) formData.append("file", file);
        await createExpense(formData);
        toast.success("Expense recorded");
      }
      onSaved();
      onClose();
    } catch {
      setError(isEdit ? "Could not update expense" : "Could not record expense");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.panel}>
        <div className={styles.header}>
          <span>{isEdit ? "Edit Expense" : "Add Expense"}</span>
          <CloseButton onClick={onClose} />
        </div>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formWrapper}>
          {isEdit && (
            <div className={styles.notice}>
              Amount, date, category, payment method, and cash/bank account cannot
              be changed after posting. Cancel the expense and record a new one to
              correct financial details.
            </div>
          )}
          {error && <div className={styles.apiError}>{error}</div>}
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Expense Date</label>
              <input
                type="date"
                className={styles.input}
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
                max={todayYmd()}
                disabled={isEdit}
              />
            </div>
            <div className={styles.field}>
              <label>Expense Category</label>
              <select
                className={styles.select}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={isEdit}
              >
                {categories.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.field}>
            <label>Expense Description</label>
            <input
              className={styles.input}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What was this expense for?"
            />
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={styles.input}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                disabled={isEdit}
              />
            </div>
            <div className={styles.field}>
              <label>Payment Method</label>
              <select
                className={styles.select}
                value={paymentMethod}
                onChange={(e) => handlePaymentMethodChange(e.target.value)}
                disabled={isEdit}
              >
                {paymentMethods.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Paid From</label>
              <select
                className={styles.select}
                value={paidFrom}
                onChange={(e) => setPaidFrom(e.target.value)}
                disabled={isEdit}
              >
                {paidFromOptions.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label>Cash/Bank Account</label>
              <select
                className={styles.select}
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                disabled={isEdit}
              >
                {filteredAccounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.account_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Vendor (optional)</label>
              <select
                className={styles.select}
                value={vendorId}
                onChange={(e) => handleVendorChange(e.target.value)}
              >
                <option value="">Select vendor</option>
                {vendors.map((v) => {
                  const id = v.vendor_id || v.user_id;
                  return (
                    <option key={id} value={id}>
                      {v.company_name || v.username || id}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className={styles.field}>
              <label>Vendor / Payee</label>
              <input
                className={styles.input}
                value={vendorPayee}
                onChange={(e) => setVendorPayee(e.target.value)}
                placeholder="Payee name"
              />
            </div>
          </div>
          <div className={styles.field}>
            <label>Reference Number</label>
            <input
              className={styles.input}
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder="UTR, cheque no., bill no."
            />
          </div>
          <div className={styles.field}>
            <label>Notes</label>
            <textarea
              className={styles.textarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes"
            />
          </div>
          <div className={`${styles.field} ${styles.fileRow}`}>
            <label>Supporting Document</label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file && <span className={styles.fileName}>{file.name}</span>}
          </div>
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={loading}>
              Close
            </button>
            <button type="submit" className={styles.submitBtn} disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Save Changes" : "Record Expense"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddExpensePanel;
